// src/pages/TransactionHistoryPage.js
import React, { useState, useEffect } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import TransactionCardList from './components/TransactionCardList';
import TransactionDetails from './components/TransactionDetails';
import AdminLayout from './components/AdminLayout';

const ITEMS_PER_PAGE = 10;

const TransactionHistoryPage = () => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    perPage: ITEMS_PER_PAGE,
    currentPage: 1,
    from: 0,
    to: 0,
    total: 0,
    lastPage: 1,
    prevPage: null,
    nextPage: null
  });
  
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTxn, setSelectedTxn] = useState(null);

  const [searchParams] = useSearchParams();
  const abc = searchParams.get('abc');
  const xyz = searchParams.get('xyz');

  // Show error if required params are missing
  useEffect(() => {
    if (!abc || !xyz) {
      setError('Missing required parameters (abc/xyz) in URL');
      setLoading(false);
    }
  }, [abc, xyz]);

  // Fetch purchase history when component mounts or when dependencies change
  useEffect(() => {
    const loadTransactions = async () => {
      if (!abc || !xyz) {
        setError('Missing abc or xyz parameter in URL');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError('');
        
        const response = await fetch(
          `https://api.gamegems.co/web/games/purchase-history?abc=${abc}&xyz=${xyz}&page=${pagination.currentPage}&per_page=${pagination.perPage}`
        );

        if (!response.ok) {
          throw new Error('Failed to fetch transaction history');
        }

        const data = await response.json();
        console.log('API Response:', data);

        if (data.status && data.data) {
          setTransactions(data.data);
          if (data.pagination) {
            setPagination({
              perPage: data.pagination.perPage,
              currentPage: data.pagination.currentPage,
              from: data.pagination.from,
              to: data.pagination.to,
              total: data.pagination.total,
              lastPage: data.pagination.lastPage,
              prevPage: data.pagination.prevPage,
              nextPage: data.pagination.nextPage
            });
          }
        } else {
          setError('No transaction data available');
          setTransactions([]);
        }
      } catch (err) {
        setError('Failed to load transaction history');
        console.error('Error:', err);
      } finally {
        setLoading(false);
      }
    };

    loadTransactions();
  }, [abc, xyz, pagination.currentPage]);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.lastPage) {
      setPagination(prev => ({
        ...prev,
        currentPage: newPage
      }));
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Generate page numbers for pagination
  const getPageNumbers = () => {
    const pages = [];
    const maxPagesToShow = 5; // Maximum number of page numbers to show
    let startPage = Math.max(1, pagination.currentPage - Math.floor(maxPagesToShow / 2));
    let endPage = Math.min(pagination.lastPage, startPage + maxPagesToShow - 1);
    
    if (endPage - startPage + 1 < maxPagesToShow) {
      startPage = Math.max(1, endPage - maxPagesToShow + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    return pages;
  };

  const filteredTransactions = transactions.filter((txn) => {
    const searchTermLower = searchTerm.toLowerCase();
    const idStr = String(txn?.id || '').toLowerCase();
    const userStr = String(txn?.user || '').toLowerCase();
    
    return idStr.includes(searchTermLower) || 
           userStr.includes(searchTermLower);
  });

  return (
    <AdminLayout abc={abc} xyz={xyz}>
        <div className="p-3 sm:p-4 md:p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 sm:mb-6">
            <h1 className="text-xl sm:text-2xl font-bold text-white mb-3 sm:mb-0">Transaction History</h1>
            
            {/* Search Input */}
            <div className="w-full sm:w-64">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
                <input
                  type="text"
                  placeholder="Search transactions..."
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setPagination(prev => ({ ...prev, currentPage: 1 }));
                  }}
                  className="w-full pl-10 pr-3 py-2 rounded-lg bg-gray-900 text-white text-sm sm:text-base focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
            </div>
          </div>

          {loading ? (
            <div className="text-center py-10">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500 mx-auto"></div>
              <p className="mt-2 text-gray-400">Loading transactions...</p>
            </div>
          ) : error ? (
            <div className="text-center py-10 text-red-500">
              {error}
            </div>
          ) : (
            <>
              <TransactionCardList 
                transactions={filteredTransactions} 
                onClickCard={setSelectedTxn} 
              />

              {pagination.total > 0 && (
                <div className="mt-4">
                  {/* Pagination Info - Mobile */}
                  <div className="text-xs text-center text-gray-400 mb-3 sm:hidden">
                    Page {pagination.currentPage} of {pagination.lastPage}
                  </div>
                  
                  {/* Pagination Info - Desktop */}
                  <div className="text-xs sm:text-sm text-gray-400 mb-3 hidden sm:block">
                    Showing <span className="font-medium">{pagination.from + 1}</span> to{' '}
                    <span className="font-medium">
                      {Math.min(pagination.to, pagination.total)}
                    </span>{' '}
                    of <span className="font-medium">{pagination.total}</span> results
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <button
                      onClick={() => handlePageChange(pagination.currentPage - 1)}
                      disabled={!pagination.prevPage}
                      className={`px-3 py-1.5 text-sm rounded-md ${
                        !pagination.prevPage
                          ? 'bg-gray-900 text-gray-500 cursor-not-allowed'
                          : 'bg-gray-700 text-white hover:bg-gray-600'
                      }`}
                    >
                      &larr; Previous
                    </button>
                    
                    <div className="hidden sm:flex space-x-1">
                      {getPageNumbers().map((page) => (
                        <button
                          key={page}
                          onClick={() => handlePageChange(page)}
                          className={`w-8 h-8 flex items-center justify-center text-sm rounded ${
                            pagination.currentPage === page
                              ? 'bg-blue-600 text-white'
                              : 'bg-gray-700 text-white hover:bg-gray-600'
                          }`}
                        >
                          {page}
                        </button>
                      ))}
                    </div>
                    
                    <button
                      onClick={() => handlePageChange(pagination.currentPage + 1)}
                      disabled={!pagination.nextPage}
                      className={`px-3 py-1.5 text-sm rounded-md ${
                        !pagination.nextPage
                          ? 'bg-gray-900 text-gray-500 cursor-not-allowed'
                          : 'bg-gray-700 text-white hover:bg-gray-600'
                      }`}
                    >
                      Next &rarr;
                    </button>
                  </div>
                </div>
              )}
            </>
          )}

          <TransactionDetails
            transaction={selectedTxn}
            onClose={() => setSelectedTxn(null)}
          />
        </div>
    </AdminLayout>
  );
};

export default TransactionHistoryPage;